import { useEffect, useMemo, useState } from 'react'
import { differenceInDays, startOfDay } from 'date-fns'
import { useParams } from 'react-router-dom'
import type { TreatmentWeek, TreatmentMonth, TreatmentTracker } from '../../../../redux/flows/portal/currentPatient'
import { updateVisitFrequencyAsync, getNextAppointmentDayAsync } from '../../../../redux/flows/portal/currentPatient'
import { postTreatmentPlanType, updateTreatmentTracker } from '../../../../redux/flows/portal/lobby'
import { useAppSelector, useAppDispatch } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'
import { isMember } from '../../../../utils/isMember'

export const useAssessment = () => {
  const dispatch = useAppDispatch()
  const { visitId } = useParams<{ visitId: string }>()
  const patientDetails = useAppSelector((state) => state.lobbyPortal.patientDetails)
  const updateVisitFrequencyStatus = useAppSelector((state) => state.currentPatientPortal.updateVisitFrequencyStatus)
  const nextAppointmentDay = useAppSelector((state) => state.currentPatientPortal.nextAppointmentDay)
  const nextAppointmentDayStatus = useAppSelector((state) => state.currentPatientPortal.nextAppointmentDayStatus)
  const [tabValue, setTabValue] = useState<'S' | 'W'>('S')

  const currentPatient = patientDetails

  // Fetch next appointment day when visit is loaded or changes
  useEffect(() => {
    if (visitId) {
      dispatch(getNextAppointmentDayAsync(visitId))
    }
  }, [visitId, dispatch])

  // Fetch next appointment day after visit frequency is successfully updated
  useEffect(() => {
    if (updateVisitFrequencyStatus === APIStatus.fulfilled && visitId) {
      dispatch(getNextAppointmentDayAsync(visitId))
    }
  }, [updateVisitFrequencyStatus, visitId, dispatch])

  // Abbreviation map
  const abbreviationMap: Record<string, string> = {
    'Headaches/Migraines': 'Headaches',
    'High Blood Pressure': 'High BP',
    'Joint Replacement': 'Joint Repl.',
    'Heart Disease': 'Heart Dis.',
    'Fused/Fixated Joints': 'Fused/Fix',
    'Herniated Disc': 'Hern. Disc',
    Osteoporosis: 'Osteo',
    Osteopenia: 'Osteo',
    'Seizure Disorder': 'Seizure',
    Tuberculosis: 'Tuber',
    'Upper Back Pain': 'Upper Bk.',
    'Middle Back Pain': 'Mid Back',
    'Lower Back Pain': 'Lower Bk.',
    'Shoulder Pain': 'Shoulder',
  }

  const selectedQuestions = useMemo(() => {
    if (!currentPatient?.questionResponses) return []

    const prioritizedOptions = currentPatient.questionResponses.flatMap((qr: any) =>
      qr.responseOptions
        .filter((ro: any) => (ro.priority || 0) > 0)
        .map((ro: any) => ({
          text: ro.option.text,
          priority: ro.priority || 0,
        })),
    )

    const sorted = prioritizedOptions
      .sort((a: any, b: any) => a.priority - b.priority)
      .map((item: any) => {
        const text = item.text
        return abbreviationMap[text] ?? text
      })

    return sorted.slice(0, 4)
  }, [currentPatient, abbreviationMap])

  const treatmentType = useMemo(() => {
    return currentPatient?.treatmentTracker?.data?.type || 'symptomatic'
  }, [currentPatient?.treatmentTracker])

  const currentWeekNumber = useMemo(() => {
    const treatmentTracker = currentPatient?.treatmentTracker
    if (!treatmentTracker?.data) return 0

    if (treatmentType === 'symptomatic' && treatmentTracker.data.weeks) {
      if (treatmentTracker.data.weeks.length === 0) return 0
      const weekNumbers = treatmentTracker.data.weeks.map((week: any) => week.weekNumber)
      return Math.max(...weekNumbers)
    } else if (treatmentType === 'preventative' && treatmentTracker.data.months) {
      if (treatmentTracker.data.months.length === 0) return 0
      const monthNumbers = treatmentTracker.data.months.map((month: any) => month.monthNumber)
      return Math.max(...monthNumbers)
    }

    return 0
  }, [currentPatient?.treatmentTracker, treatmentType])

  const periodsArrayLength = useMemo(() => {
    const treatmentTracker = currentPatient?.treatmentTracker
    if (!treatmentTracker?.data) return 0

    if (treatmentType === 'symptomatic' && treatmentTracker.data.weeks) {
      return treatmentTracker.data.weeks.length
    } else if (treatmentType === 'preventative' && treatmentTracker.data.months) {
      return treatmentTracker.data.months.length
    }

    return 0
  }, [currentPatient?.treatmentTracker, treatmentType])

  const painScaleConfig = useMemo(() => {
    const treatmentTracker = currentPatient?.treatmentTracker
    const isPreventative = treatmentType === 'preventative'
    const isSymptomatic = treatmentType === 'symptomatic'
    const weeks = treatmentTracker?.data?.weeks || []
    const months = treatmentTracker?.data?.months || []
    const enabledPeriods = periodsArrayLength
    const hasReachedPeriod12 = enabledPeriods >= 12
    const isAtWeek12 = isSymptomatic && (currentWeekNumber === 12 || enabledPeriods >= 12)

    // Helper function to get color based on symptomLevel
    const getColorFromSymptomLevel = (symptomLevel: string | null): string => {
      if (symptomLevel === 'high' || symptomLevel === null) {
        return '#E94235'
      } else if (symptomLevel === 'medium') {
        return '#FF7E1D'
      } else if (symptomLevel === 'low') {
        return '#FFCE00'
      }
      return '#E94235' // default to red for high/null
    }

    // For symptomatic at week 12, all boxes should be #007F7C
    // if (isAtWeek12) {
    //   return {
    //     boxes: Array.from({ length: 12 }, (_, i) => ({
    //       weekNumber: i + 1,
    //       label: String(i + 1),
    //       bg: '#007F7C',
    //       border: '#007F7C',
    //       textColor: 'var(--color-text-secondary)',
    //       disabled: false,
    //     })),
    //     symptomaticEnabled: false,
    //     correctiveEnabled: false,
    //   }
    // }

    if (hasReachedPeriod12) {
      if (isPreventative) {
        // For preventative, use #007F7C with increasing opacity
        return {
          boxes: Array.from({ length: 12 }, (_, i) => {
            const monthNum = i + 1
            const opacity = 0.45 + (monthNum - 1) * 0.05
            return {
              weekNumber: monthNum,
              label: `M${monthNum}`,
              bg: `rgba(0, 127, 124, ${opacity})`,
              border: `rgba(0, 127, 124, ${opacity})`,
              textColor: 'var(--color-text-secondary)',
              disabled: false,
            }
          }),
          symptomaticEnabled: false,
          correctiveEnabled: false,
        }
      }
    }

    const boxes = Array.from({ length: 12 }, (_, i) => {
      const periodNum = i + 1
      let bg = 'var(--color-bg)'
      let border = 'var(--color-disabled)'
      let textColor = 'var(--color-disabled)'
      let disabled = true
      let label = String(periodNum)

      if (isPreventative) {
        label = `M${periodNum}`
      }

      if (periodNum <= enabledPeriods) {
        disabled = false

        if (isPreventative) {
          // For preventative, use #007F7C with increasing opacity
          // Month 1: 0.45, Month 2: 0.50, ..., Month 12: 1.0
          const opacity = 0.45 + (periodNum - 1) * 0.05
          bg = `rgba(0, 127, 124, ${opacity})`
          border = `rgba(0, 127, 124, ${opacity})`
          textColor = 'var(--color-text-secondary)'
        } else {
          // For symptomatic, use symptomLevel-based colors
          const weekData = weeks.find((week: any) => week.weekNumber === periodNum)
          const symptomLevel = weekData?.symptomLevel ?? null
          const color = getColorFromSymptomLevel(symptomLevel)
          bg = color
          border = color
          textColor = 'var(--color-text-secondary)'
        }
      }

      return { weekNumber: periodNum, label, bg, border, textColor, disabled }
    })

    const symptomaticEnabled = !isPreventative && enabledPeriods >= 1 && enabledPeriods <= 4
    const correctiveEnabled = !isPreventative && enabledPeriods >= 5 && enabledPeriods <= 11

    return { boxes, symptomaticEnabled, correctiveEnabled }
  }, [periodsArrayLength, currentPatient?.treatmentTracker, treatmentType, currentWeekNumber])

  // Days until next renewal (subscription expiry); null if no expiry date or already past
  const daysUntilRenewal = useMemo(() => {
    const expiresAt = currentPatient?.userPaymentPlan?.subscriptionExpiresAt
    if (!expiresAt) return null
    const days = differenceInDays(startOfDay(new Date(expiresAt)), startOfDay(new Date()))
    return days >= 0 ? days : null
  }, [currentPatient?.userPaymentPlan?.subscriptionExpiresAt])

  // Single bar: when 14–8 days away from next renewal (not when symptomatic at week 12 — we flash all instead)
  const shouldFlashSingleWeek = useMemo(() => {
    const isSymptomatic = treatmentType === 'symptomatic'
    // Don't flash single week if we're at week 12 (we'll flash all instead)
    if (isSymptomatic && (currentWeekNumber === 12 || periodsArrayLength >= 12)) {
      return false
    }
    if (daysUntilRenewal === null) return false
    return daysUntilRenewal >= 8 && daysUntilRenewal <= 14
  }, [daysUntilRenewal, treatmentType, currentWeekNumber, periodsArrayLength])

  // All bars: when 7–0 days away from next renewal, or when symptomatic at week 12
  const shouldFlashAllWeeks = useMemo(() => {
    const isSymptomatic = treatmentType === 'symptomatic'
    // Flash all weeks if at week 12 for symptomatic
    if (isSymptomatic && (currentWeekNumber === 12 || periodsArrayLength >= 12)) {
      return true
    }
    if (daysUntilRenewal === null) return false
    return daysUntilRenewal >= 0 && daysUntilRenewal <= 7
  }, [daysUntilRenewal, treatmentType, currentWeekNumber, periodsArrayLength])

  // Metric tile highlights: DSL yellow above 7 days since last adjustment, LTD yellow
  // below 4 visits in the last 30 days. When both are yellow, they flash together.
  // ltd is null until the backend backfill runs — that must not read as 0.
  // pg returns numeric/decimal columns as strings, so coerce rather than typeof-check.
  const toNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  const dstValue = toNumber(currentPatient?.metrics?.dst)
  const ltdValue = toNumber(currentPatient?.metrics?.ltd)
  const dslHighlight = dstValue !== null && dstValue > 7
  // Null means "no metrics row yet" (new patients), NOT "no visits" — it must never
  // flag. Only a real number under 4 highlights, so 0 is yellow but null stays plain.
  const ltdHighlight = ltdValue !== null && ltdValue < 4
  // Flashing is reserved for the compound signal: only when BOTH tiles are yellow do
  // they flash, together. A single yellow tile stays solid.
  const metricsFlash = dslHighlight && ltdHighlight
  // LTD always shows a number — 0 is the default, including for patients with no
  // metrics row yet. Only the highlight distinguishes a real 0 from a missing one.
  const ltdDisplay = ltdValue ?? 0

  // Check if toggle should be disabled (preventative type means no going back)
  const isToggleDisabled = useMemo(() => {
    return treatmentType === 'preventative'
  }, [treatmentType])

  // Convert weeks to months: approximately 4 weeks = 1 month
  const convertWeeksToMonths = (weeks: TreatmentWeek[]): TreatmentMonth[] => {
    const months: TreatmentMonth[] = []
    const weeksPerMonth = 4

    // Group weeks into months
    for (let i = 0; i < weeks.length; i += weeksPerMonth) {
      const monthWeeks = weeks.slice(i, i + weeksPerMonth)
      const monthNumber = Math.floor(i / weeksPerMonth) + 1

      // Calculate visits in the month
      const visitsInTheMonth = monthWeeks.reduce((sum, week) => sum + week.visitsIntheWeek, 0)

      // Check if any week in this month is a scan week
      const isScanMonth = monthWeeks.some((week) => week.isScanWeek)

      // Use the symptomLevel from the last week in the month, or null if not available
      const symptomLevel = monthWeeks[monthWeeks.length - 1]?.symptomLevel ?? null

      months.push({
        monthNumber,
        visitsInTheMonth,
        isScanMonth,
        symptomLevel,
      })
    }

    return months
  }

  // // Handle tab change
  // const handleTabChange = (value: 'S' | 'W') => {
  //   // Prevent changing from W to S if type is preventative
  //   if (treatmentType === 'preventative' && value === 'S') {
  //     return
  //   }

  //   // If changing from S to W and type is symptomatic, convert to preventative
  //   if (tabValue === 'S' && value === 'W' && treatmentType === 'symptomatic' && currentPatient) {
  //     const weeks = currentPatient.treatmentTracker?.data?.weeks || []

  //     if (weeks.length > 0) {
  //       const months = convertWeeksToMonths(weeks)
  //       const treatmentStartedAt = currentPatient.treatmentTracker?.data?.treatmentStartedAt || new Date().toISOString()
  //       const subscriptionStartedAt =
  //         currentPatient.treatmentTracker?.data?.subscriptionStartedAt || new Date().toISOString()

  //       // Create the updated treatment tracker payload
  //       const updatedTracker: TreatmentTracker = {
  //         code: currentPatient.treatmentTracker?.code || 'success',
  //         message: currentPatient.treatmentTracker?.message || null,
  //         data: {
  //           type: 'preventative',
  //           treatmentStartedAt,
  //           subscriptionStartedAt,
  //           months: months,
  //         },
  //       }

  //       // Immediately update tab value to W
  //       setTabValue('W')

  //       // Optimistically update treatment tracker in Redux
  //       dispatch(updateTreatmentTracker(updatedTracker))

  //       // Dispatch the action to update treatment tracker via API
  //       dispatch(
  //         postTreatmentPlanType({
  //           patientId: currentPatient.patient.id,
  //           data: {
  //             type: 'preventative',
  //             treatmentStartedAt,
  //             subscriptionStartedAt,
  //             months: months,
  //           },
  //           payload: updatedTracker,
  //         }),
  //       )
  //     }
  //   } else {
  //     setTabValue(value)
  //   }
  // }

  // Sync tab value with treatment type
  // useEffect(() => {
  //   if (treatmentType === 'preventative') {
  //     setTabValue('W')
  //   } else if (treatmentType === 'symptomatic') {
  //     setTabValue('S')
  //   }
  // }, [treatmentType])

  // Calculate weeks count based on subscription/treatment start date
  const calculateWeeksCount = (startDate: Date | null): number => {
    if (!startDate) {
      return 0
    }

    const start = new Date(startDate)
    const today = new Date()

    // Calculate the difference in milliseconds
    const diffMs = today.getTime() - start.getTime()

    // Calculate the difference in days
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    // Calculate weeks (round down)
    const weeksCount = Math.floor(diffDays / 7)

    const currentWeek = weeksCount + 1 // Week 1 starts from day 0-6

    return currentWeek
  }

  // Calculate months count based on subscription/treatment start date
  const calculateMonthsCount = (startDate: Date | null): number => {
    if (!startDate) {
      return 1 // Default to 1 if no start date
    }

    const start = new Date(startDate)
    const today = new Date()

    // Get the year and month for both dates
    const startYear = start.getFullYear()
    const startMonth = start.getMonth()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth()

    // Calculate the difference in months
    const monthsDiff = (currentYear - startYear) * 12 + (currentMonth - startMonth)

    // If same month, count is always 1
    if (monthsDiff === 0) {
      return 1
    }

    // Calculate the target date/time in the current month (same day, hour, minute, second as start)
    // Handle edge case where start day doesn't exist in target month (e.g., Jan 31 -> Feb)
    const startDay = start.getDate()
    const lastDayOfTargetMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
    const targetDay = Math.min(startDay, lastDayOfTargetMonth)

    const targetDate = new Date(
      currentYear,
      currentMonth,
      targetDay,
      start.getHours(),
      start.getMinutes(),
      start.getSeconds(),
      start.getMilliseconds(),
    )

    // Compare current time with target time
    // If current time is before the target time, we haven't reached the same date/time yet
    // If current time is at or after the target time, we've passed it
    let monthsCount
    if (today < targetDate) {
      // We're in a later month but haven't reached the same date/time yet
      monthsCount = monthsDiff
    } else {
      // We've reached or passed the same date/time in the current month
      monthsCount = monthsDiff + 1
    }

    return monthsCount
  }

  // Calculate which weeks fall within the 4th month
  const calculateWeeksInMonth4 = (
    startDate: Date | null,
  ): {
    month4StartDate: Date | null
    month4EndDate: Date | null
    weekRange: { startWeek: number; endWeek: number } | null
    possibleWeeks: number[]
    lastWeekOfMonth4: number | null
  } => {
    if (!startDate) {
      return {
        month4StartDate: null,
        month4EndDate: null,
        weekRange: null,
        possibleWeeks: [],
        lastWeekOfMonth4: null,
      }
    }

    const start = new Date(startDate)

    // Calculate month 4 start date (3 months after start)
    const month4StartDate = new Date(start)
    month4StartDate.setMonth(month4StartDate.getMonth() + 3)

    // Calculate month 4 end date (4 months after start, but before the same day/time)
    const month4EndDate = new Date(start)
    month4EndDate.setMonth(month4EndDate.getMonth() + 4)

    // Calculate which week number corresponds to month 4 start
    const diffMsStart = month4StartDate.getTime() - start.getTime()
    const diffDaysStart = Math.floor(diffMsStart / (1000 * 60 * 60 * 24))
    const startWeek = Math.floor(diffDaysStart / 7) + 1

    // Calculate which week number corresponds to month 4 end
    const diffMsEnd = month4EndDate.getTime() - start.getTime()
    const diffDaysEnd = Math.floor(diffMsEnd / (1000 * 60 * 60 * 24))
    const endWeek = Math.floor(diffDaysEnd / 7) + 1

    // Generate array of possible weeks in month 4
    const possibleWeeks: number[] = []
    for (let week = startWeek; week < endWeek; week++) {
      possibleWeeks.push(week)
    }

    // Calculate last week of month 4
    const lastWeekOfMonth4 = possibleWeeks.length > 0 ? Math.max(...possibleWeeks) : endWeek ? endWeek - 1 : null

    return {
      month4StartDate,
      month4EndDate,
      weekRange: { startWeek, endWeek },
      possibleWeeks,
      lastWeekOfMonth4,
    }
  }

  // Calculate subscription progress based on subscriptionStartedAt
  // Uses millisecond precision to determine which month period we're in
  // Shows tick when Free Month is active (matching mobile app logic)
  const subscriptionProgress = useMemo(() => {
    const subscriptionStartedAt = currentPatient?.treatmentTracker?.data?.subscriptionStartedAt
    const treatmentStartedAt = currentPatient?.treatmentTracker?.data?.treatmentStartedAt
    const startDate = subscriptionStartedAt || treatmentStartedAt

    if (!startDate) {
      return {
        showProgress: false,
        showCircle: false,
        progressPercent: 0,
        displayNumber: null,
      }
    }

    const start = new Date(startDate)
    const now = new Date()

    // Helper function to add months to a date (handles month overflow correctly)
    const addMonths = (date: Date, months: number): Date => {
      const result = new Date(date)
      result.setMonth(result.getMonth() + months)
      return result
    }

    // Determine which month period we're in by comparing with exact month boundaries
    // Month 1: from startDate to startDate + 1 month
    // Month 2: from startDate + 1 month to startDate + 2 months
    // Month 3: from startDate + 2 months to startDate + 3 months
    // Month 4: from startDate + 3 months to startDate + 4 months
    // Month 5+: after startDate + 4 months

    let currentMonth = 0
    for (let i = 1; i <= 5; i++) {
      const monthEndDate = addMonths(start, i)
      if (now < monthEndDate) {
        currentMonth = i
        break
      }
    }

    // If we've passed all 5 months, currentMonth will be 0
    if (currentMonth === 0) {
      currentMonth = 5 // Month 5+
    }

    // Check if Free Month is active (for new members with symptomatic type)
    // Matching mobile app logic: show tick from week 12 until end of month 4
    const isNewMember = currentPatient?.treatmentTracker?.data?.isNewMember
    const treatmentType = currentPatient?.treatmentTracker?.data?.type
    const weeksLength = currentPatient?.treatmentTracker?.data?.weeks?.length || 0

    // Show tick from week 12 until end of month 4 (same as mobile app)
    // Hide tick when month 5 starts
    if (isNewMember && treatmentType === 'symptomatic' && currentMonth < 5) {
      const calculatedWeek = calculateWeeksCount(start)
      const month4Weeks = calculateWeeksInMonth4(start)
      const lastWeekOfMonth4 = month4Weeks.lastWeekOfMonth4

      // Free Month is active when (matching mobile app exactly):
      // 1. weeksLength >= 12 OR
      // 2. calculatedWeek >= 12 && calculatedWeek <= lastWeekOfMonth4
      // But only show until end of month 4 (hide when month 5 starts)
      const isInMonth4Range = calculatedWeek >= 12 && calculatedWeek <= (lastWeekOfMonth4 || Infinity)
      const isFreeMonthActive = weeksLength >= 12 || isInMonth4Range

      // Show tick when Free Month is active (from week 12 until end of month 4)
      // The currentMonth < 5 check ensures it hides when month 5 starts
      if (isFreeMonthActive) {
        return {
          showProgress: false,
          showCircle: true,
          progressPercent: 100,
          displayNumber: null,
        }
      }
    }

    // Month 1: 25% progress, show "1"
    if (currentMonth === 1) {
      return {
        showProgress: true,
        showCircle: false,
        progressPercent: 25,
        displayNumber: 1,
      }
    }

    // Month 2: 50% progress, show "2"
    if (currentMonth === 2) {
      return {
        showProgress: true,
        showCircle: false,
        progressPercent: 50,
        displayNumber: 2,
      }
    }

    // Month 3: 75% progress, show "3"
    if (currentMonth === 3) {
      return {
        showProgress: true,
        showCircle: false,
        progressPercent: 75,
        displayNumber: 3,
      }
    }

    // Month 4: Hide progress, show circle with tick (handled above for eligible patients)
    // For non-eligible patients, hide both
    if (currentMonth === 4) {
      // If not eligible for Free Month (already handled above), hide both
      return {
        showProgress: false,
        showCircle: false,
        progressPercent: 0,
        displayNumber: null,
      }
    }

    // Month 5+: Hide both (tick is hidden when month 5+ is reached)
    return {
      showProgress: false,
      showCircle: false,
      progressPercent: 0,
      displayNumber: null,
    }
  }, [
    currentPatient?.treatmentTracker?.data?.subscriptionStartedAt,
    currentPatient?.treatmentTracker?.data?.treatmentStartedAt,
    currentPatient?.treatmentTracker?.data?.isNewMember,
    currentPatient?.treatmentTracker?.data?.type,
    currentPatient?.treatmentTracker?.data?.weeks,
  ])

  // Get visitFrequency from API response, default to 'medium'
  const visitFrequency = currentPatient?.treatmentTracker?.data?.visitFrequency || 'medium'

  // Map visitFrequency to display letter (H/M/L)
  const getVisitFrequencyDisplay = (frequency: 'high' | 'medium' | 'low'): 'H' | 'M' | 'L' => {
    if (frequency === 'high') return 'H'
    if (frequency === 'low') return 'L'
    return 'M' // default to 'medium'
  }

  const visitFrequencyDisplay = getVisitFrequencyDisplay(visitFrequency)

  // Cycle visitFrequency: high -> medium -> low -> high
  const handleCycleDayTagLevel = () => {
    // Prevent clicks while API is pending to avoid race conditions
    // This ensures only one API call is in progress at a time
    if (updateVisitFrequencyStatus === APIStatus.pending) {
      return // Ignore click - API call already in progress
    }

    const currentFrequency = visitFrequency
    let newFrequency: 'high' | 'medium' | 'low'

    if (currentFrequency === 'high') {
      newFrequency = 'medium'
    } else if (currentFrequency === 'medium') {
      newFrequency = 'low'
    } else {
      newFrequency = 'high'
    }

    // Optimistic update: Update UI immediately for better UX
    // This happens before the API call, so the UI feels instant
    if (currentPatient?.treatmentTracker) {
      const updatedTracker = {
        ...currentPatient.treatmentTracker,
        data: {
          ...currentPatient.treatmentTracker.data,
          visitFrequency: newFrequency,
        },
      }
      dispatch(updateTreatmentTracker(updatedTracker))
    }

    // Call API to update visit frequency
    // Store original frequency for revert on failure
    // Status will be set to 'pending' immediately, preventing rapid clicks
    if (currentPatient?.patient?.id) {
      dispatch(
        updateVisitFrequencyAsync({
          patientId: currentPatient.patient.id,
          visitFrequency: newFrequency,
          originalFrequency: currentFrequency,
        }),
      )
      // Next appointment day will be fetched automatically via useEffect
      // when updateVisitFrequencyStatus becomes 'fulfilled'
    }
  }

  return {
    currentPatient,
    selectedQuestions,
    tabValue,
    // setTabValue: handleTabChange,
    painScaleConfig,
    currentWeekNumber,
    shouldFlashSingleWeek,
    shouldFlashAllWeeks,
    dslHighlight,
    ltdHighlight,
    metricsFlash,
    ltdDisplay,
    isToggleDisabled,
    subscriptionProgress,
    visitFrequencyDisplay,
    handleCycleDayTagLevel,
    updateVisitFrequencyStatus,
    nextAppointmentDay,
    nextAppointmentDayStatus,
  }
}
